import React, { FC, ReactElement, useContext, useEffect, useState } from 'react'
import QRCode from 'react-qr-code'
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, MessageContext, ReactState, UserInfoContext } from '../../../../../React/Common/Contexts'
import { MFALanguage } from '../../../../../../i18n'
import { MessageContextType } from '../../../../../React/Common/Contexts/MessageContext'
import { UserInfoContextType } from '../../../../../React/Common/Contexts/UserInfoContext'
import { IsStringEmpty, IsVarEmpty, isVarNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { requestJSON } from '../../../../../NonReact/common/API/REST/RESTClient'
import {
    APIElements,
    APIPathParams,
    mfaEnableURL,
    mfaOTPURL,
    mfaSecretCodeURL,
    mfaSetupURL
} from '../../../../../NonReact/constants/ApiConstants'
import { RESTMethods } from '../../../../../NonReact/constants/REST'
import { signOut } from '../../../../../React/Common/API/UserSignInOutAPI'
import { MFASecretCodeAPIReponse } from '../Types/Types'
import { HelpPopoverText } from '../../../../../React/Common/Components/HelpPopoverText/HelpPopoverText'
import { LanguageUtils } from '../../../../../React/Common/Utils/LanguageUtils'
import { OTPInput } from '../../../../../React/Common/Components/OTPInput/OTPInput'
import Loader from '../../../../../React/Common/Components/Loader'
import { RoundedButton } from '../../../Atoms/RoundedButton/RoundedButton'
import { AUTH_ROUTE_PATH } from '../../../../Constants/Constants'
import { redirectToURL } from '../../../../../NonReact/utils/LocationUtils'
import { URLConstructor } from '../../../../../NonReact/utils/URLBuilder'
import { JSONResponse } from '../../../../../NonReact/common/Types/Types'

const OTP_AUTH_CODE_NAME: string = 'Insights'

export const MFALoginAndSetup: FC = (): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(MFALanguage.NAME)

    const { displayError }: MessageContextType = useContext(MessageContext)
    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const location: Location = useLocation()
    const navigate: NavigateFunction = useNavigate()

    const [qrCode, setQRCode]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [mfaResponseCode, setMfaResponseCode]: ReactState<Nullable<string>> = useState<Nullable<string>>()
    const [isLoading, setIsLoading]: ReactState<boolean> = useState<boolean>(true)
    const [errorMessage, setErrorMessage]: ReactState<Nullable<string>> = useState<Nullable<string>>()

    const isContinueDisabled: boolean = IsStringEmpty(mfaResponseCode) || mfaResponseCode?.length !== 6
    const isSetupMode: boolean = isVarNotEmpty(userInfo) && IsVarEmpty(userInfo?.MFAChallengeName)
    const isSetupDisplayed: boolean = isSetupMode && !IsStringEmpty(qrCode)
    const isError: boolean = !IsStringEmpty(errorMessage)

    const otpAuthCodeURL: string = `otpauth://totp/${OTP_AUTH_CODE_NAME}:${userInfo?.Username}?secret=${qrCode}&issuer=Cognito`

    const errorHandler = (error: Error): void => {
        setErrorMessage(error.message)
        setIsLoading(false)
    }

    const onSetup = (): void => {
        if (!isContinueDisabled) {
            requestJSON(mfaSetupURL, {
                method: RESTMethods.POST,
                body: {
                    OtpCode: mfaResponseCode
                }
            })
                .then((): void => {
                    setErrorMessage(undefined)
                    requestJSON(mfaEnableURL, {
                        method: RESTMethods.PUT
                    })
                        .then((): void =>
                            redirectToURL(
                                new URLConstructor({
                                    pathParams: [APIElements.CCS, APIElements.APP_V2, APIPathParams.MFA_SUCCESS]
                                })
                            )
                        )
                        .catch(errorHandler)
                })
                .catch(errorHandler)
        }
    }

    const onLogin = (): void => {
        if (!isContinueDisabled) {
            requestJSON(mfaOTPURL, {
                method: RESTMethods.POST,
                body: {
                    OtpCode: mfaResponseCode
                }
            })
                .then((): void =>
                    redirectToURL(
                        new URLConstructor({
                            pathParams: [APIElements.CCS, APIElements.APP_V2, APIPathParams.MFA_SUCCESS]
                        })
                    )
                )
                .catch(errorHandler)
        }

        return undefined
    }

    const onCancel = (): void =>
        signOut((): void =>
            navigate(AUTH_ROUTE_PATH, {
                replace: true,
                state: { from: location }
            })
        )

    const onKeyDown = (event?: KeyboardEvent): void => {
        if (event?.key === 'Enter' && !isContinueDisabled) {
            if (isSetupDisplayed) {
                onSetup()
            } else {
                onLogin()
            }
        } else if (event?.key === 'Escape') {
            onCancel()
        }
    }

    useEffect((): void => {
        if (userInfo && isSetupMode) {
            requestJSON<MFASecretCodeAPIReponse>(mfaSecretCodeURL, {
                method: RESTMethods.POST
            })
                .then((response: JSONResponse<MFASecretCodeAPIReponse>): void => {
                    setQRCode(response?.SecretCode)
                    setIsLoading(false)
                })
                .catch(displayError)
        } else {
            setIsLoading(false)
        }
    }, [userInfo, isSetupMode])

    return (
        <div className='mfa-setup' onKeyDown={(event: React.KeyboardEvent): void => onKeyDown(event.nativeEvent)}>
            {!isLoading ? (
                <>
                    {isSetupDisplayed ? (
                        <>
                            <h1>{language.get(MFALanguage.MFA_Setup_Title)}</h1>
                            <div className='mfa-description'>
                                {language.get(MFALanguage.MFA_Setup_Description1)}
                                <HelpPopoverText
                                    content={LanguageUtils.formatLanguageEntry(
                                        language.get(MFALanguage.MFA_Authenticator_Help)
                                    )}
                                    popoverPlacement='bottom'
                                >
                                    {language.get(MFALanguage.MFA_Setup_Description2)}
                                </HelpPopoverText>
                            </div>
                            <div className='qr-code'>
                                <QRCode value={otpAuthCodeURL} />
                            </div>
                        </>
                    ) : (
                        <>
                            <h1>{language.get(MFALanguage.MFA_Login_Title)}</h1>
                            <div className='mfa-description'>{language.get(MFALanguage.MFA_Login_Description)}</div>
                        </>
                    )}
                    <div className='mfa-response-code-container'>
                        {isSetupDisplayed ? (
                            <div className='mfa-description'>{language.get(MFALanguage.MFA_Setup_Description3)}</div>
                        ) : (
                            <></>
                        )}
                        <OTPInput className={isError ? 'mfa-is-error' : ''} setState={setMfaResponseCode} />
                        {isError ? <div className='mfa-error-message'>{errorMessage}</div> : <></>}
                    </div>
                    <div className='mfa-action-container'>
                        {!IsStringEmpty(qrCode) ? (
                            <>
                                <RoundedButton className='mfa-button' onClick={onCancel}>
                                    {language.get(MFALanguage.MFA_Setup_Cancel)}
                                </RoundedButton>
                                <RoundedButton className='mfa-button' onClick={onSetup} disabled={isContinueDisabled}>
                                    {language.get(MFALanguage.MFA_Setup_Continue)}
                                </RoundedButton>
                            </>
                        ) : (
                            <>
                                <RoundedButton className='mfa-button' onClick={onCancel}>
                                    {language.get(MFALanguage.MFA_Login_Cancel)}
                                </RoundedButton>
                                <RoundedButton className='mfa-button' onClick={onLogin} disabled={isContinueDisabled}>
                                    {language.get(MFALanguage.MFA_Login_Continue)}
                                </RoundedButton>
                            </>
                        )}
                    </div>
                </>
            ) : (
                <Loader />
            )}
        </div>
    )
}
